import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import useAppStore from "../../store/app.store";
import api from "../../services/api";
import toast, { Toaster } from "react-hot-toast";

const moeda = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pct = (a, b) => b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0;
const hoje = () => new Date().toISOString().split("T")[0];
const mesAtual = () => new Date().toISOString().slice(0, 7);

const MOTIVOS = [
  "Preco alto", "Concorrente", "Sem budget", "Produto inadequado",
  "Sem retorno do lead", "Prazo nao atende", "Ja tem fornecedor", "Outros",
];

function BarraProgresso({ valor, meta, label, cor = "#6C4CF5", mostrarValor = false, formatarFn }) {
  const p = pct(valor, meta);
  const barCor = p >= 100 ? "#4ADE80" : p >= 70 ? cor : p >= 40 ? "#FBBF24" : "#F87171";
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: barCor }}>
          {formatarFn ? formatarFn(valor) : valor} / {formatarFn ? formatarFn(meta) : meta} ({p}%)
        </span>
      </div>
      <div style={{ height: 10, borderRadius: 999, background: "rgba(255,255,255,.07)", overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 999, background: barCor, width: `${p}%`, transition: "width .5s" }} />
      </div>
    </div>
  );
}

function Funil({ leads, contatos, vendas }) {
  const etapas = [
    { label: "Leads Recebidos", valor: leads, cor: "#6C4CF5", largura: 100 },
    { label: "Contatos Realizados", valor: contatos, cor: "#38BDF8", largura: 75, taxa: pct(contatos, leads) },
    { label: "Vendas Fechadas", valor: vendas, cor: "#4ADE80", largura: 50, taxa: pct(vendas, contatos) },
  ];
  return (
    <div style={{ padding: "20px 0" }}>
      {etapas.map((e, i) => (
        <div key={i} style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <div style={{
              height: 44, borderRadius: 6, background: `${e.cor}22`, border: `1px solid ${e.cor}44`,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "width .5s", width: `${e.largura}%`, margin: "0 auto",
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: e.cor }}>{e.valor}</span>
            </div>
          </div>
          <p style={{ textAlign: "center", fontSize: 11, color: "#64748B", marginBottom: 4 }}>{e.label}</p>
          {e.taxa !== undefined && (
            <p style={{ textAlign: "center", fontSize: 10, color: e.taxa >= 30 ? "#4ADE80" : e.taxa >= 15 ? "#FBBF24" : "#F87171", fontWeight: 700 }}>
              ↓ {e.taxa}% de conversao
            </p>
          )}
        </div>
      ))}
      <div style={{ textAlign: "center", marginTop: 12, padding: "8px 16px", background: "rgba(74,222,128,.08)", borderRadius: 8, border: "1px solid rgba(74,222,128,.2)" }}>
        <p style={{ fontSize: 11, color: "#64748B" }}>Conversao geral (leads → vendas)</p>
        <p style={{ fontSize: 20, fontWeight: 700, color: "#4ADE80" }}>{pct(vendas, leads)}%</p>
      </div>
    </div>
  );
}

function FormRegistro({ token, metaId, registros, onEnviado }) {
  const [enviando, setEnviando] = useState(false);
  const [f, setF] = useState({
    data: hoje(), leads_recebidos: "", contatos: "", vendas: "",
    receita: "", motivos_perda: [], observacoes: "",
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const jaEnviouHoje = registros.some(r => r.meta_id === metaId && r.data === f.data);

  const toggleMotivo = (m) => setF(p => ({
    ...p,
    motivos_perda: p.motivos_perda.includes(m)
      ? p.motivos_perda.filter(x => x !== m)
      : [...p.motivos_perda, m],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!f.leads_recebidos) return toast.error("Informe o numero de leads recebidos.");
    setEnviando(true);
    try {
      const { data } = await api.post(`/dados/publico/comercial/${token}/registros`, {
        ...f, leads_recebidos: +f.leads_recebidos, contatos: +f.contatos, vendas: +f.vendas, receita: +f.receita,
      });
      toast.success("Relatorio enviado com sucesso!");
      onEnviado(data);
      setF({ data: hoje(), leads_recebidos: "", contatos: "", vendas: "", receita: "", motivos_perda: [], observacoes: "" });
    } catch (err) {
      toast.error(err.response?.data?.error || "Nao foi possivel enviar. Tente novamente.");
    } finally { setEnviando(false); }
  };

  return (
    <div style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 16, padding: 24, marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: "#E2E8F4", marginBottom: 20 }}>
        Enviar Relatorio do Dia
      </h3>
      {jaEnviouHoje && (
        <div style={{ background: "rgba(74,222,128,.08)", border: "1px solid rgba(74,222,128,.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: "#4ADE80" }}>Voce ja enviou o relatorio desta data. Selecione outra data para enviar novamente ou edite no painel da agencia.</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label style={lbl}>Data *</label>
            <input className="form-input" type="date" value={f.data} onChange={e => set("data", e.target.value)} required />
          </div>
          <div>
            <label style={lbl}>Leads recebidos *</label>
            <input className="form-input" type="number" min="0" value={f.leads_recebidos} onChange={e => set("leads_recebidos", e.target.value)} placeholder="0" required />
          </div>
          <div>
            <label style={lbl}>Contatos realizados</label>
            <input className="form-input" type="number" min="0" value={f.contatos} onChange={e => set("contatos", e.target.value)} placeholder="0" />
          </div>
          <div>
            <label style={lbl}>Vendas fechadas</label>
            <input className="form-input" type="number" min="0" value={f.vendas} onChange={e => set("vendas", e.target.value)} placeholder="0" />
          </div>
          <div>
            <label style={lbl}>Receita gerada (R$)</label>
            <input className="form-input" type="number" min="0" step="0.01" value={f.receita} onChange={e => set("receita", e.target.value)} placeholder="0,00" />
          </div>
          {+f.vendas < +f.contatos && +f.contatos > 0 && (
            <div className="col-span-2">
              <label style={lbl}>Motivos de nao fechamento (selecione os que se aplicam)</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "12px 14px", background: "rgba(255,255,255,.03)", borderRadius: 8, border: "1px solid #2A3652" }}>
                {MOTIVOS.map(m => (
                  <button key={m} type="button" onClick={() => toggleMotivo(m)}
                    style={{ fontSize: 12, padding: "5px 12px", borderRadius: 999, border: "none", cursor: "pointer",
                      background: f.motivos_perda.includes(m) ? "#F87171" : "rgba(255,255,255,.06)",
                      color: f.motivos_perda.includes(m) ? "#fff" : "#94A3B8",
                      fontWeight: f.motivos_perda.includes(m) ? 600 : 400 }}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="col-span-2">
            <label style={lbl}>Observacoes</label>
            <textarea className="form-input" rows={2} value={f.observacoes} onChange={e => set("observacoes", e.target.value)} placeholder="Comentarios adicionais sobre o dia..." />
          </div>
        </div>
        <button type="submit" disabled={enviando} style={{ width: "100%", padding: "12px 0", background: "linear-gradient(90deg,#6C4CF5,#7A5CFF)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700, cursor: enviando ? "wait" : "pointer", opacity: enviando ? 0.7 : 1 }}>
          {enviando ? "Enviando..." : "Enviar Relatorio"}
        </button>
      </form>
    </div>
  );
}

const lbl = { fontSize: 12, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 6 };

export default function ComercialPublico() {
  const { token } = useParams();
  const clientes         = useAppStore(s => s.clientes);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [dados, setDados] = useState({ carregando: true, meta: null, registros: [] });

  useEffect(() => {
    api.get(`/dados/publico/comercial/${token}`)
      .then(({ data }) => setDados({ carregando: false, meta: data.meta, registros: data.registros || [] }))
      .catch(() => setDados({ carregando: false, meta: null, registros: [] }));
  }, [token]);

  const { meta, registros } = dados;
  const cliente = meta ? clientes.find(c => c.id === meta.cliente_id) : null;

  const regsDoMes = useMemo(() => {
    if (!meta) return [];
    return registros.filter(r => r.meta_id === meta.id && r.data?.slice(0, 7) === meta.mes);
  }, [registros, meta]);

  const totais = useMemo(() => regsDoMes.reduce((acc, r) => ({
    leads:    acc.leads + (r.leads_recebidos || 0),
    contatos: acc.contatos + (r.contatos || 0),
    vendas:   acc.vendas + (r.vendas || 0),
    receita:  acc.receita + (r.receita || 0),
  }), { leads: 0, contatos: 0, vendas: 0, receita: 0 }), [regsDoMes]);

  // Calculos por periodo
  const diasNoMes = new Date(meta?.mes ? new Date(meta.mes + "-01").getFullYear() : 2026, meta?.mes ? new Date(meta.mes + "-01").getMonth() + 1 : 1, 0).getDate();
  const diaAtual  = new Date().getDate();
  const semanas   = Math.ceil(diaAtual / 7);
  const quinzenas = diaAtual <= 15 ? 1 : 2;

  const metaDiaria    = meta ? Math.ceil(meta.vendas_meta / diasNoMes) : 0;
  const metaSemanal   = meta ? Math.ceil(meta.vendas_meta / 4) : 0;
  const metaQuinzenal = meta ? Math.ceil(meta.vendas_meta / 2) : 0;

  const vendasSemana   = useMemo(() => {
    const inicioSemana = new Date(); inicioSemana.setDate(inicioSemana.getDate() - inicioSemana.getDay());
    return regsDoMes.filter(r => new Date(r.data) >= inicioSemana).reduce((a, r) => a + (r.vendas || 0), 0);
  }, [regsDoMes]);

  const vendasQuinzena = useMemo(() => {
    return regsDoMes.filter(r => {
      const d = new Date(r.data).getDate();
      return quinzenas === 1 ? d <= 15 : d > 15;
    }).reduce((a, r) => a + (r.vendas || 0), 0);
  }, [regsDoMes, quinzenas]);

  const ultimosRegistros = [...regsDoMes].sort((a, b) => b.data.localeCompare(a.data)).slice(0, 7);

  if (dados.carregando) {
    return <div style={{ minHeight: "100vh", background: "#0B1220", color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}>Carregando…</div>;
  }

  if (!meta || !cliente) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0B1220" }}>
        <Toaster position="top-right" />
        <div style={{ textAlign: "center", color: "#fff" }}>
          <div style={{ marginBottom: 16, opacity: 0.5 }}><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
          <p style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Link nao encontrado</p>
          <p style={{ color: "#64748B", fontSize: 14 }}>Verifique o link enviado pela sua agencia.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0B1220", color: "#E2E8F4" }}>
      <Toaster position="top-right" />

      {/* Header */}
      <div style={{ background: "#131D31", borderBottom: "1px solid #2A3652", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16 }}>
        <div>
          <span style={{ fontFamily: "'Georgia','Times New Roman',serif", fontSize: 18, fontWeight: 700, color: "#fff" }}>
            Legacy <span style={{ color: "#6C4CF5" }}>Hub</span>
          </span>
        </div>
        <div style={{ width: 1, height: 28, background: "#2A3652" }} />
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: "#E2E8F4" }}>{cliente.nome_fantasia || cliente.razao_social}</p>
          <p style={{ fontSize: 11, color: "#64748B" }}>Acompanhamento Comercial — {meta.mes}</p>
        </div>
        <button onClick={() => setMostrarForm(v => !v)}
          style={{ marginLeft: "auto", padding: "8px 18px", background: "linear-gradient(90deg,#6C4CF5,#7A5CFF)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          {mostrarForm ? "Ver Dashboard" : "Enviar Relatorio do Dia"}
        </button>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 20px" }}>

        {mostrarForm ? (
          <FormRegistro token={token} metaId={meta.id} registros={registros}
            onEnviado={(novo) => { setDados(d => ({ ...d, registros: [...d.registros, novo] })); setMostrarForm(false); }} />
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ marginBottom: 24 }}>
              {[
                { l: "Leads do Mes", v: totais.leads, meta: meta.leads_meta, cor: "#6C4CF5" },
                { l: "Contatos", v: totais.contatos, meta: meta.contatos_meta || meta.leads_meta, cor: "#38BDF8" },
                { l: "Vendas", v: totais.vendas, meta: meta.vendas_meta, cor: "#4ADE80" },
                { l: "Receita", v: moeda(totais.receita), meta: moeda(meta.receita_meta), cor: "#FBBF24", str: true },
              ].map((k, i) => (
                <div key={i} style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 14, padding: "16px 18px" }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{k.l}</p>
                  <p style={{ fontSize: 26, fontWeight: 700, color: k.cor, lineHeight: 1 }}>{k.v}</p>
                  <p style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>Meta: {k.meta}</p>
                  {!k.str && <div style={{ height: 3, borderRadius: 999, background: "rgba(255,255,255,.06)", marginTop: 8 }}>
                    <div style={{ height: "100%", borderRadius: 999, background: k.cor, width: `${pct(k.v, k.meta)}%` }} />
                  </div>}
                </div>
              ))}
            </div>

            {/* Progresso por periodo + Funil */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ marginBottom: 24 }}>
              <div style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: "#94A3B8", marginBottom: 16 }}>Progresso de Vendas por Periodo</h3>
                <BarraProgresso valor={totais.vendas / Math.max(diaAtual, 1) | 0} meta={metaDiaria} label={`Media diaria (hoje e dia ${diaAtual})`} cor="#6C4CF5" />
                <BarraProgresso valor={vendasSemana} meta={metaSemanal} label="Semana atual" cor="#38BDF8" />
                <BarraProgresso valor={vendasQuinzena} meta={metaQuinzenal} label={`${quinzenas}a quinzena`} cor="#FBBF24" />
                <BarraProgresso valor={totais.vendas} meta={meta.vendas_meta} label="Meta mensal" cor="#4ADE80" />
              </div>

              <div style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: "#94A3B8", marginBottom: 4 }}>Funil de Conversao — {meta.mes}</h3>
                <Funil leads={totais.leads} contatos={totais.contatos} vendas={totais.vendas} />
              </div>
            </div>

            {/* Historico */}
            {ultimosRegistros.length > 0 && (
              <div style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 14, padding: 20, marginBottom: 24 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: "#94A3B8", marginBottom: 14 }}>Ultimos Relatorios Enviados</h3>
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #2A3652" }}>
                      {["Data", "Leads", "Contatos", "Vendas", "Receita", "Conv."].map((h, i) => (
                        <th key={i} style={{ padding: "8px 12px", textAlign: i === 0 ? "left" : "center", fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ultimosRegistros.map(r => (
                      <tr key={r.id} style={{ borderBottom: "1px solid rgba(42,54,82,.4)" }}>
                        <td style={{ padding: "10px 12px", color: "#E2E8F4" }}>{r.data}</td>
                        <td style={{ padding: "10px 12px", color: "#6C4CF5", fontWeight: 600, textAlign: "center" }}>{r.leads_recebidos}</td>
                        <td style={{ padding: "10px 12px", color: "#38BDF8", fontWeight: 600, textAlign: "center" }}>{r.contatos}</td>
                        <td style={{ padding: "10px 12px", color: "#4ADE80", fontWeight: 600, textAlign: "center" }}>{r.vendas}</td>
                        <td style={{ padding: "10px 12px", color: "#FBBF24", fontWeight: 600, textAlign: "center" }}>{moeda(r.receita)}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>
                          <span style={{ fontSize: 11, color: pct(r.vendas, r.leads_recebidos) >= 20 ? "#4ADE80" : "#F87171", fontWeight: 700 }}>
                            {pct(r.vendas, r.leads_recebidos)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <button onClick={() => setMostrarForm(true)}
                style={{ padding: "12px 32px", background: "linear-gradient(90deg,#6C4CF5,#7A5CFF)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                Enviar Relatorio de Hoje
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
